import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { computeOrderSplit } from '@shared/payout-split';

import { queryClient } from '@/lib/queryClient';
import { authedJson, authedFetch } from '@/lib/http';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface AmbassadorUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

interface CreatorRow {
  id: string;
  displayName?: string | null;
  email?: string | null;
}

interface ClassRow {
  id: string;
  title: string;
}

interface Referral {
  id: string;
  ambassadorUserId: string;
  ambassadorFirstName: string | null;
  ambassadorEmail: string | null;
  creatorId: string;
  creatorDisplayName: string | null;
  commissionMode: string | null;
  commissionPct: string | null;
  commissionFixedCents: number | null;
  status: string;
}

const usd = (c: number) => `$${(c / 100).toFixed(2)}`;

export function AmbassadorsAdmin() {
  const { toast } = useToast();

  // Some admin list endpoints return a paginated { data, total } object rather than
  // a bare array — coerce defensively so a shape mismatch can't crash the section.
  const asArray = <T,>(v: unknown): T[] =>
    Array.isArray(v) ? (v as T[]) : (((v as { data?: T[] } | null)?.data ?? []) as T[]);

  const { data: ambassadorsRaw } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/ambassadors'],
    queryFn: () => authedJson('/api/admin/financials/ambassadors'),
  });
  const { data: creatorsRaw } = useQuery<unknown>({
    queryKey: ['/api/admin/creators'],
    queryFn: () => authedJson('/api/admin/creators'),
  });
  const { data: referralsRaw } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/ambassador-referrals'],
    queryFn: () => authedJson('/api/admin/financials/ambassador-referrals'),
  });
  const { data: classesRaw } = useQuery<unknown>({
    queryKey: ['/api/admin/classes'],
    queryFn: () => authedJson('/api/admin/classes'),
  });
  const ambassadors = asArray<AmbassadorUser>(ambassadorsRaw);
  const creators = asArray<CreatorRow>(creatorsRaw);
  const referrals = asArray<Referral>(referralsRaw);
  const classes = asArray<ClassRow>(classesRaw);

  // Create-referral form
  const [ambassadorUserId, setAmbassadorUserId] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [mode, setMode] = useState<'pct_of_net' | 'fixed'>('pct_of_net');
  const [pct, setPct] = useState('5');
  const [fixedDollars, setFixedDollars] = useState('5');

  const createReferral = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { ambassadorUserId, creatorId, commissionMode: mode };
      if (mode === 'pct_of_net') body.commissionPct = Number(pct);
      else body.commissionFixedCents = Math.round(Number(fixedDollars) * 100);
      const res = await authedFetch('/api/admin/financials/ambassador-referrals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Referral created' });
      setAmbassadorUserId('');
      setCreatorId('');
      queryClient.invalidateQueries({ queryKey: ['/api/admin/financials/ambassador-referrals'] });
    },
    onError: (e: Error) =>
      toast({ title: 'Could not create referral', description: e.message, variant: 'destructive' }),
  });

  const endReferral = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/financials/ambassador-referrals/${id}/end`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to end referral');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Referral ended' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/financials/ambassador-referrals'] });
    },
  });

  // Grant the ambassador capability to any user (by email) — keeps their role.
  const [grantEmail, setGrantEmail] = useState('');
  const grantAmbassador = useMutation({
    mutationFn: async () => {
      const res = await authedFetch('/api/admin/financials/ambassadors/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIdOrEmail: grantEmail.trim() }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Ambassador access granted' });
      setGrantEmail('');
      queryClient.invalidateQueries({ queryKey: ['/api/admin/financials/ambassadors'] });
    },
    onError: (e: Error) =>
      toast({ title: 'Could not grant access', description: e.message, variant: 'destructive' }),
  });

  // Per-class assignment (overrides the creator-level referral for that class).
  const [assignClassId, setAssignClassId] = useState('');
  const [assignAmbassadorId, setAssignAmbassadorId] = useState('');
  const [assignMode, setAssignMode] = useState<'pct_of_net' | 'fixed'>('pct_of_net');
  const [assignPct, setAssignPct] = useState('5');
  const [assignFixed, setAssignFixed] = useState('5');

  const assignClass = useMutation({
    mutationFn: async (clear: boolean) => {
      const body: Record<string, unknown> = clear
        ? { ambassadorUserId: null }
        : { ambassadorUserId: assignAmbassadorId, commissionMode: assignMode };
      if (!clear) {
        if (assignMode === 'pct_of_net') body.commissionPct = Number(assignPct);
        else body.commissionFixedCents = Math.round(Number(assignFixed) * 100);
      }
      const res = await authedFetch(`/api/admin/financials/classes/${assignClassId}/ambassador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      return res.json();
    },
    onSuccess: (_d, clear) => {
      toast({ title: clear ? 'Class ambassador cleared' : 'Class ambassador set' });
    },
    onError: (e: Error) =>
      toast({ title: 'Could not update class', description: e.message, variant: 'destructive' }),
  });

  const creatorLabel = (c: CreatorRow) => c.displayName || c.email || c.id;

  // Live split preview
  const [previewPrice, setPreviewPrice] = useState('28');
  const [previewCreatorPct, setPreviewCreatorPct] = useState('90');
  const preview = useMemo(() => {
    const net = Math.round(Number(previewPrice) * 100) || 0;
    const stripeFee = net > 0 ? Math.round(net * 0.029 + 30) : 0;
    const ambassador =
      mode === 'fixed'
        ? { mode: 'fixed' as const, fixedCents: Math.round(Number(fixedDollars) * 100) }
        : { mode: 'pct_of_net' as const, pct: Number(pct) };
    return computeOrderSplit({
      netCents: net,
      stripeFeeCents: stripeFee,
      creatorPct: Number(previewCreatorPct) || 0,
      ambassador,
    });
  }, [previewPrice, previewCreatorPct, mode, pct, fixedDollars]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Ambassadors</h2>
        <p className="text-sm text-muted-foreground">
          Referral commissions (carved from Kalovate's cut — creator/venue pay is never affected).
        </p>
      </div>

      {/* Grant ambassador access (additive — keeps their role) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Grant ambassador access</CardTitle>
          <CardDescription>
            Give any account (including existing creators/venues) ambassador access by email. Their
            primary role is unchanged.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="user@example.com"
            value={grantEmail}
            onChange={e => setGrantEmail(e.target.value)}
          />
          <Button
            onClick={() => grantAmbassador.mutate()}
            disabled={!grantEmail.trim() || grantAmbassador.isPending}
            className="shrink-0"
          >
            {grantAmbassador.isPending ? 'Granting…' : 'Grant access'}
          </Button>
        </CardContent>
      </Card>

      {/* Create referral */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Add a referral commission</CardTitle>
          <CardDescription>
            Assign the ambassador role first (Users), then link them to a creator here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Ambassador</Label>
              <select
                aria-label="Ambassador"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={ambassadorUserId}
                onChange={e => setAmbassadorUserId(e.target.value)}
              >
                <option value="">Select…</option>
                {ambassadors.map(a => (
                  <option key={a.id} value={a.id}>
                    {[a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || a.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Creator</Label>
              <select
                aria-label="Creator"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={creatorId}
                onChange={e => setCreatorId(e.target.value)}
              >
                <option value="">Select…</option>
                {creators.map(c => (
                  <option key={c.id} value={c.id}>
                    {creatorLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Commission type</Label>
              <select
                aria-label="Commission type"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={mode}
                onChange={e => setMode(e.target.value as 'pct_of_net' | 'fixed')}
              >
                <option value="pct_of_net">% of sales</option>
                <option value="fixed">Fixed $ / session</option>
              </select>
            </div>
            <div>
              <Label>{mode === 'pct_of_net' ? 'Percent' : 'Dollars per session'}</Label>
              {mode === 'pct_of_net' ? (
                <Input
                  className="mt-1"
                  type="number"
                  min="0"
                  max="100"
                  value={pct}
                  onChange={e => setPct(e.target.value)}
                />
              ) : (
                <Input
                  className="mt-1"
                  type="number"
                  min="0"
                  value={fixedDollars}
                  onChange={e => setFixedDollars(e.target.value)}
                />
              )}
            </div>
          </div>
          <Button
            onClick={() => createReferral.mutate()}
            disabled={!ambassadorUserId || !creatorId || createReferral.isPending}
          >
            {createReferral.isPending ? 'Saving…' : 'Add referral'}
          </Button>
        </CardContent>
      </Card>

      {/* Per-class assignment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assign an ambassador to a specific class</CardTitle>
          <CardDescription>
            Overrides the creator-level referral for that class only. Leave the ambassador blank and
            press Clear to remove it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Class</Label>
              <select
                aria-label="Class"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={assignClassId}
                onChange={e => setAssignClassId(e.target.value)}
              >
                <option value="">Select…</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Ambassador</Label>
              <select
                aria-label="Ambassador for class"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={assignAmbassadorId}
                onChange={e => setAssignAmbassadorId(e.target.value)}
              >
                <option value="">Select…</option>
                {ambassadors.map(a => (
                  <option key={a.id} value={a.id}>
                    {[a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || a.id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Commission type</Label>
              <select
                aria-label="Class commission type"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={assignMode}
                onChange={e => setAssignMode(e.target.value as 'pct_of_net' | 'fixed')}
              >
                <option value="pct_of_net">% of sales</option>
                <option value="fixed">Fixed $ / session</option>
              </select>
            </div>
            <div>
              <Label>{assignMode === 'pct_of_net' ? 'Percent' : 'Dollars per session'}</Label>
              <Input
                className="mt-1"
                type="number"
                min="0"
                value={assignMode === 'pct_of_net' ? assignPct : assignFixed}
                onChange={e =>
                  assignMode === 'pct_of_net'
                    ? setAssignPct(e.target.value)
                    : setAssignFixed(e.target.value)
                }
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => assignClass.mutate(false)}
              disabled={!assignClassId || !assignAmbassadorId || assignClass.isPending}
            >
              {assignClass.isPending ? 'Saving…' : 'Assign to class'}
            </Button>
            <Button
              variant="outline"
              onClick={() => assignClass.mutate(true)}
              disabled={!assignClassId || assignClass.isPending}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live split preview */}
      <Card className="border-primary/40 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Live split preview</CardTitle>
          <CardDescription>
            Confirms the Stripe fee is always covered before the ambassador is paid.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Ticket price ($)</Label>
              <Input
                className="mt-1"
                type="number"
                value={previewPrice}
                onChange={e => setPreviewPrice(e.target.value)}
              />
            </div>
            <div>
              <Label>Creator %</Label>
              <Input
                className="mt-1"
                type="number"
                value={previewCreatorPct}
                onChange={e => setPreviewCreatorPct(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
            {[
              ['Stripe fee', preview.processingFeeCents],
              ['Ambassador', preview.ambassadorEarningsCents],
              ['Kalovate', preview.platformFeeCents],
              ['Creator', preview.creatorEarningsCents],
              ['Venue', preview.venueEarningsCents],
            ].map(([label, cents]) => (
              <div key={label as string} className="rounded-lg border bg-background p-2">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-semibold tabular-nums">{usd(cents as number)}</p>
              </div>
            ))}
          </div>
          {preview.ambassadorCapped && (
            <p className="text-xs font-medium text-orange-600">
              Ambassador capped on this price so the Stripe fee stays covered.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Referral list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Active &amp; past referrals</CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No referrals yet.</p>
          ) : (
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Ambassador</th>
                    <th className="py-2 pr-4 font-medium">Creator</th>
                    <th className="py-2 pr-4 font-medium">Commission</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        {r.ambassadorFirstName || r.ambassadorEmail || '—'}
                      </td>
                      <td className="py-2 pr-4">{r.creatorDisplayName || r.creatorId}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {r.commissionMode === 'fixed'
                          ? `${usd(r.commissionFixedCents ?? 0)}/session`
                          : `${Number(r.commissionPct ?? 0)}%`}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={r.status === 'active' ? 'default' : 'secondary'}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="py-2 text-right">
                        {r.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => endReferral.mutate(r.id)}
                            disabled={endReferral.isPending}
                          >
                            End
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
